import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Sparkles, X } from 'lucide-react';
import { getLevelTitle, getLevelUpMessage } from '../utils/motivationalMessages';
import { Button } from './ui/Button';
interface LevelUpModalProps {
  isOpen: boolean;
  level: number;
  xp: number;
  onClose: () => void;
}
export function LevelUpModal({
  isOpen,
  level,
  xp,
  onClose
}: LevelUpModalProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  // Accessibility: Escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  return <AnimatePresence data-id="element-492">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-493">
            {/* Confetti Effect */}
            {showConfetti && <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-494">
                {[...Array(50)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-full" style={{
            background: ['#fbbf24', '#f59e0b', '#4f46e5', '#10b981', '#fb7185'][i % 5],
            left: `${Math.random() * 100}%`,
            top: '-10%'
          }} initial={{
            y: 0,
            opacity: 1,
            rotate: 0
          }} animate={{
            y: window.innerHeight + 100,
            opacity: 0,
            rotate: 360,
            transition: {
              duration: 2 + Math.random() * 2,
              delay: Math.random() * 0.5,
              ease: 'easeOut'
            }
          }} data-id="element-495" />)}
              </div>}

            {/* Modal */}
            <motion.div initial={{
          scale: 0.5,
          opacity: 0,
          rotateY: -180
        }} animate={{
          scale: 1,
          opacity: 1,
          rotateY: 0
        }} exit={{
          scale: 0.5,
          opacity: 0,
          rotateY: 180
        }} transition={{
          type: 'spring',
          damping: 20,
          stiffness: 200
        }} className="bg-white rounded-3xl p-8 max-w-md w-full relative shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-496">
              <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors" aria-label="Sluiten" data-id="element-497">
                <X className="w-6 h-6" data-id="element-498" />
              </button>

              <div className="text-center" data-id="element-499">
                {/* Animated Trophy */}
                <motion.div initial={{
              scale: 0,
              rotate: -180
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              delay: 0.2,
              type: 'spring',
              damping: 10
            }} className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full mb-6 shadow-lg relative" data-id="element-500">
                  <Trophy className="w-12 h-12 text-white" data-id="element-501" />

                  {/* Sparkles around trophy */}
                  {[0, 1, 2, 3].map(i => <motion.div key={i} className="absolute" style={{
                top: '50%',
                left: '50%',
                transform: `rotate(${i * 90}deg) translateY(-50px)`
              }} animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.2
              }} data-id="element-502">
                      <Sparkles className="w-4 h-4 text-amber-400" data-id="element-503" />
                    </motion.div>)}
                </motion.div>

                <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-3xl font-bold text-slate-900 mb-2" data-id="element-504">
                  Level Up! 🎉
                </motion.h2>

                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.4
            }} className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full mb-4" data-id="element-505">
                  <Star className="w-5 h-5 fill-indigo-500" data-id="element-506" />
                  <span className="font-bold text-lg" data-id="element-507">Level {level}</span>
                </motion.div>

                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5
            }} className="text-2xl font-bold text-amber-600 mb-3" data-id="element-508">
                  {getLevelTitle(level)}
                </motion.p>

                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} className="text-slate-600 mb-6" data-id="element-509">
                  {getLevelUpMessage(level)}
                </motion.p>

                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.7
            }} className="bg-slate-50 rounded-xl p-4 mb-6" data-id="element-510">
                  <div className="flex items-center justify-center gap-2 text-slate-700" data-id="element-511">
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" data-id="element-512" />
                    <span className="font-bold text-lg" data-id="element-513">{xp} XP</span>
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.8
            }} data-id="element-514">
                  <Button onClick={onClose} variant="primary" size="lg" fullWidth data-id="element-515">
                    Doorgaan met oefenen! 🚀
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}